import { t } from '@/utils/i18n'
import React, { useState, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Toast } from 'antd-mobile'
import Thumb from '@/components/Thumb'
import SearchHighlight from '@/components/SearchHighlight/SearchHighlight'
import ProductCellPrice from '@/components/ProductCellPrice/ProductCellPrice'
import ListStepper from '@/components/ListStepper/ListStepper'
import { image as imageUtil, utils, productUtil } from '@/utils'
import useCorpSettingStore from '@/stores/corp-setting'
import type { UnitPriceItem, ShowPlaceOrderProps } from '@/types/product'
import styles from './ProductCell.module.less'
import ShippingTypeLabel from '@/components/ShippingTypeLabel'
import useRefState from '@/hooks/useRefState'

interface ProductCellProps {
  listMode: boolean
  item: any
  isFilterPage?: boolean
  index: number
  searchText: string
  onShowPlaceOrder: (option: ShowPlaceOrderProps) => void
  onShowUnitModal: (option: object) => void
  onUpdateProd: (value: string, index: number) => void
}

interface Thumb {
  url: string
  backupUrl?: string
}

const { imgFormatter } = imageUtil
const { to, px2rem } = utils

const ProductCell: React.FC<ProductCellProps> = memo((props) => {
  const { corpSetting } = useCorpSettingStore()
  const {
    stockShowOn,
    stockOn,
    salesSlipOn,
    salesOn,
    pricePrecision,
    cloudStoreStockType,
    qualityPrecision,
  } = corpSetting

  const navigate = useNavigate()
  const {
    listMode,
    item,
    isFilterPage,
    searchText,
    index,
    onShowPlaceOrder,
    onShowUnitModal,
    onUpdateProd,
  } = props
  const [thumbObj, setThumbObj] = useState<Thumb>({
    url: '',
  })
  const [lastImgUrl, setLastImgUrl] = useState<string>('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isMultiPrice, setIsMultiPrice, isMultiPriceRef] =
    useRefState<boolean>(false)
  const [chooseUnit, setChooseUnit] = useState<UnitPriceItem>({
    priceText: '',
    originPrice: 0,
    price: 0,
    linePrice: 0,
    unitRatio: 0,
  })

  const itemClassName = `${styles['card-item']} ${
    listMode ? styles['list-style'] : styles['block-style']
  } ${isFilterPage ? styles['big'] : ''}`
  const titleClassName = `${styles['list-title']} ${
    listMode ? 'ep-2' : 'ep-2 ' + styles['big']
  }`
  const blockStyleWidth = 267
  const itemStyle = {
    width: listMode ? '100%' : px2rem(blockStyleWidth),
  }

  useEffect(() => {
    const { productStatus } = props.item.cloudProduct

    setIsMultiPrice((productStatus & 128) === 128)
    initThumb()
    initChooseUnit()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.item])

  const handleGoDetail = (productId: string) => {
    navigate(`/product/detail/${productId}`)
  }

  async function initThumb() {
    const { imageList, video } = item.cloudProduct
    const imgUrl = (imageList && imageList[0]) || video

    if (imgUrl === lastImgUrl) return

    const [err, res] = await to(imgFormatter(imgUrl, 260))

    setLastImgUrl(imgUrl)
    setThumbObj({ url: err ? imgUrl : res })
  }

  // 根据默认单位索引取单位对象
  function initChooseUnit() {
    const currentChooseUnit = productUtil.setChooseUnit(
      item,
      isMultiPriceRef.current,
      cloudStoreStockType,
      qualityPrecision,
      pricePrecision,
    )
    setChooseUnit(currentChooseUnit)
  }

  function handleUnitSelect(index: number) {
    const product = item.cloudProduct

    if (stockOn && product.productStock < product.productMoqQty) {
      Toast.show(t('总库存小于起订量'))
      return
    }

    if (hasMultipleSpecs()) {
      // 如果是多规格，弹出模态修改数量框
      handleOpenPlaceOrder(index)
    } else {
      // 如果是单规格，直接切换单位
      const { item: product } = props
      onShowUnitModal({
        showUnitModal: true,
        product,
        chooseUnit,
      })
    }
  }

  // 是否多规格
  function hasMultipleSpecs() {
    return (
      item.cloudProduct.specDetailList &&
      item.cloudProduct.specDetailList.length
    )
  }

  // 弹出选购框
  function handleOpenPlaceOrder(index: number) {
    const { item: product } = props
    onShowPlaceOrder({
      showPlaceOrder: true,
      product,
      index,
    })
  }

  // 更新货品,将收到的参数往上传递
  function handleUpdateProd(value: string, index: number) {
    onUpdateProd(value, index)
  }

  return (
    <div className={itemClassName} style={itemStyle}>
      <div className={styles['card-item-bd']}>
        <Thumb
          thumb={thumbObj}
          extClass={!listMode ? 'preview-box-block' : ''}
          width={listMode ? 200 : blockStyleWidth}
          height={listMode ? 200 : blockStyleWidth}
          onGoto={() => handleGoDetail(item.cloudProduct.productId)}
        />

        <div
          className={styles['list-content']}
          onClick={() => handleGoDetail(item.cloudProduct.productId)}
        >
          <div className={styles['list-content-top']}>
            <div className={titleClassName}>
              {searchText.length > 0 ? (
                <SearchHighlight
                  searchText={searchText}
                  productDesc={item.cloudProduct.productName}
                />
              ) : (
                <>
                  <p
                    className={productUtil.getRecommendClass(item.cloudProduct)}
                  >
                    {productUtil.getRecommendTag(item.cloudProduct)}
                  </p>
                  <span>{item.cloudProduct.productName || ''}</span>
                </>
              )}
            </div>
            {!!item.cloudProduct.productConcat && (
              <p className={styles['list-concat'] + ' ep'}>
                {item.cloudProduct.productConcat}
              </p>
            )}
            {item.cloudProduct.unitPriceArray.length > 1 && (
              <div className={styles['tag-list']}>
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>
                    {productUtil.getSpecification(
                      item.cloudProduct.unitPriceArray,
                    )}
                  </span>
                </div>
              </div>
            )}
            <div className={styles['tag-list']}>
              {stockShowOn && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('库存')}</span>
                  <span className={styles['tag-text']}>
                    {chooseUnit.stock} {chooseUnit.unitName}
                  </span>
                </div>
              )}

              {/* 起订量 */}
              {item.cloudProduct.productMoqQty > 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>MOQ</span>
                  <span className={styles['tag-text']}>
                    {item.cloudProduct.productMoqQty}
                    {item.cloudProduct.unitPriceArray &&
                    item.cloudProduct.unitPriceArray[
                      item.cloudProduct.productMoqUnitIndex
                    ].unitName
                      ? item.cloudProduct.unitPriceArray[
                          item.cloudProduct.productMoqUnitIndex
                        ].unitName
                      : ''}
                  </span>
                </div>
              )}
            </div>
            <div className={styles['tag-list']}>
              {/* 是否显示销量数量（基础单位） */}
              {salesOn && item.cloudProduct.saleNum != 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('销量')}</span>
                  <span className={styles['tag-text']}>
                    {chooseUnit.saleNum} {chooseUnit.unitName}
                  </span>
                </div>
              )}
              {/* 是否显示销售次数（销售单） */}
              {salesSlipOn && item.cloudProduct.orderCount != 0 && (
                <div className={styles['tag-item']}>
                  <span className={styles['tag-title']}>{t('销量')}</span>
                  <span className={styles['tag-text']}>
                    {item.cloudProduct.orderCount} {t('单')}
                  </span>
                </div>
              )}
            </div>
            <div className={styles['delivery-tag']}>
              <ShippingTypeLabel
                shippingType={item.cloudProduct.deliveryType || 0}
              />
            </div>
          </div>
          <div className={styles['price-container']}>
            <ProductCellPrice
              index={index}
              linePrice={chooseUnit.originPriceText}
              value={chooseUnit.priceText}
              unit={chooseUnit.priceUnitName || ''}
              fontSize={productUtil.getFontSize(chooseUnit.priceText || '')}
              isLastPrice={chooseUnit.isLastPrice}
              isMultiUnit={item.cloudProduct.unitPriceArray.length > 1}
              onTriggerUnitSelect={handleUnitSelect}
            />
          </div>
        </div>
      </div>
      {listMode ? (
        <div className={styles['list-stepper']}>
          <ListStepper
            hasmultiple={
              item.cloudProduct.specDetailList &&
              item.cloudProduct.specDetailList.length > 0
            }
            chooseUnit={chooseUnit}
            product={item}
            index={index}
            onOpenPlaceorder={handleOpenPlaceOrder}
            onUpdateProd={handleUpdateProd}
          />
        </div>
      ) : (
        ''
      )}
    </div>
  )
})

export default ProductCell
